import { ACL_TYPE } from '@openx/types/aclType.ts';
import type {
  InstancesByIdObject,
  InventoryTargetingContent,
  TargetableInventoryEntities,
  TargetableInventoryList,
} from '@openx/types/targeting/inventoryAndContent.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { useIsAllowed } from '@openx/utils/permission/useIsAllowed.ts';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { enqueueErrorNotification } from '@openx/utils/state/notifications/actions.ts';
import { searchInventories } from '@openx/utils/state/packages/inventories/api.ts';
import { useQuery } from '@tanstack/react-query';
import mergeWith from 'lodash/mergeWith';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import type { AnyAction } from 'redux';
import { splitIncludedExcluded } from '../../../Targeting/shared/InventoryAndContent/utils.ts';
import { useTargetingContext } from '../../context/TargetingContext.tsx';

const rootInventoriesFetcher = async (
  idByInstances,
  currentInstanceUid,
  includedAndExcludedInventoriesJoined,
  inventoryPayload,
  accountUid
) => {
  const requests: Promise<TargetableInventoryList>[] = [];

  if (idByInstances) {
    Object.keys(idByInstances).forEach(instanceUid => {
      instanceUid !== currentInstanceUid &&
        requests.push(searchInventories(true, includedAndExcludedInventoriesJoined, instanceUid, accountUid, true));
    });
  }
  requests.push(searchInventories(true, includedAndExcludedInventoriesJoined, currentInstanceUid, accountUid, true));
  const resp = await Promise.all(requests);
  return splitIncludedExcluded(resp, inventoryPayload.excludes);
};

export const useInventoriesFetch = (
  inventoryPayload: InventoryTargetingContent<TargetableInventoryEntities>,
  idByInstances?: InstancesByIdObject
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { useAccountUid, useCurrentInstanceState } = useTargetingContext();

  const accountUid = useAccountUid();
  const { currentInstanceUid, isBefInstance } = useCurrentInstanceState();
  const { isAllowed } = useIsAllowed();

  const isUserAllowed =
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.ROOT_WORKAS_ANYINSTANCE)) ||
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));

  const isBefAndRoot = isUserAllowed && isBefInstance;

  const includedAndExcludedInventoriesJoined = mergeWith(
    {},
    inventoryPayload.includes,
    inventoryPayload.excludes,
    (includesIds: string | undefined, excludesIds: string | undefined) =>
      includesIds && excludesIds ? `${includesIds},${excludesIds}` : undefined
  );

  const { data: inventories, isLoading } = useQuery<InventoryTargetingContent<TargetableInventoryList> | null>({
    queryFn: async () => {
      try {
        if (isBefAndRoot) {
          return await rootInventoriesFetcher(
            idByInstances,
            currentInstanceUid,
            includedAndExcludedInventoriesJoined,
            inventoryPayload,
            accountUid
          );
        } else {
          const resp = await Promise.all([
            searchInventories(false, includedAndExcludedInventoriesJoined, currentInstanceUid, accountUid, true),
          ]);
          return splitIncludedExcluded(resp, inventoryPayload.excludes);
        }
      } catch (err) {
        dispatch(enqueueErrorNotification(t('Failed to fetch Inventories'), err) as AnyAction);
        return null;
      }
    },
    queryKey: [
      QueryKeys.INVENTORIES,
      accountUid,
      { currentInstanceUid, isBefAndRoot, idByInstances, includedAndExcludedInventoriesJoined },
      inventoryPayload,
    ],
  });

  return {
    inventories,
    loading: isLoading,
  };
};
